import clsx from "clsx";
import { FC, useContext, useMemo } from "react";
import { useRecoilValue } from "recoil";

import { StationRouteContext, useCrudEditContext } from "@ryde/redux";
import {
  editFormDataAF,
  EditFormMode,
  formTabControlsSF,
  IRideDto,
  isInnerItemEditingAtom,
  IStationRouteDto,
  RideCrudModeKeyTuple,
  useHasPermissions,
  useIsMobile,
} from "@ryde/shared";

import { FormControlsWrapper } from "../../form.controls.wrapper";
import { StationRouteStationsPanel } from "../stations";
import { StationRouteBanner } from "./station-route.banner";
import {
  StationRouteStationsTabContentStyled,
  StationRouteTabsTabStationsStyled,
} from "./station-route.styled";
import { StationRouteTabsFixedControls } from "./station-route.tabs.fixed-controls";

export const StationRouteTabsTabStations: FC = () => {
  const { isMobile } = useIsMobile();
  const { entity, mode } = useCrudEditContext<IStationRouteDto>();
  const { rideOpenerId } = useContext(StationRouteContext);
  const currentTabControls = useRecoilValue(formTabControlsSF(entity));
  const stationRouteDto = useRecoilValue<IStationRouteDto | undefined>(
    editFormDataAF([entity, mode || EditFormMode.CrudRoute]),
  );
  const rideDto = useRecoilValue<Partial<IRideDto> | undefined>(
    editFormDataAF(RideCrudModeKeyTuple),
  );

  const { hasPermissions: hasUpdatePermission } = useHasPermissions({
    entity: "stationRoute",
    action: "update",
  });
  const isFormDisabled = !hasUpdatePermission;

  const controlsComputed = useMemo(() => {
    if (!isFormDisabled) {
      return currentTabControls;
    }

    return currentTabControls.map(x => ({
      ...x,
      formAttributes: { ...x.formAttributes, disabled: true },
    }));
  }, [currentTabControls, isFormDisabled]);

  const isCustomizedPath = Boolean(stationRouteDto?.customized);
  const shouldDisplayBanner = !isMobile && (!!rideOpenerId || isCustomizedPath);
  const isInnerItemEditing = useRecoilValue(isInnerItemEditingAtom);

  return (
    <StationRouteTabsTabStationsStyled
      className={clsx(isInnerItemEditing && "inner-item-editing")}
    >
      {shouldDisplayBanner && (
        <StationRouteBanner isCustomized={isCustomizedPath} />
      )}
      <StationRouteStationsTabContentStyled>
        {!!rideOpenerId && !!stationRouteDto?.id && rideDto && (
          <StationRouteTabsFixedControls
            stationRouteId={stationRouteDto.id}
            isFormDisabled={isFormDisabled}
            rideDto={rideDto}
          />
        )}
        <FormControlsWrapper controls={controlsComputed} />
      </StationRouteStationsTabContentStyled>
      {stationRouteDto && (
        <StationRouteStationsPanel stationRouteDto={stationRouteDto} />
      )}
    </StationRouteTabsTabStationsStyled>
  );
};
